import Component from '@glimmer/component'
import { action, get } from '@ember/object'
import { guidFor } from '@ember/object/internals'
import { service } from '@ember/service'
import { groupBy } from 'lodash'
import { tracked } from '@glimmer/tracking'
import type SubscriptionTypeService from 'teacher-ui-client/services/subscription-type'
import { GradeSelect } from 'teacher-ui-client/components/base/select/grade-position'
import { AllGradesOption } from 'teacher-ui-client/components/base/select/grade-position/options'
import { BaseSelect } from 'teacher-ui-client/components/base/select'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import { t } from 'ember-intl'
import { eq, or } from 'ember-truth-helpers'
import { on } from '@ember/modifier'
import { eventValue } from 'teacher-ui-client/helpers/event-value'
import { fullGradeName } from 'teacher-ui-client/helpers/full-grade-name'
import { array } from '@ember/helper'
import { Precinct } from 'teacher-ui-client/utils/precincts'
import { trackedFunction } from 'reactiveweb/function'
import type LessonInfoService from 'teacher-ui-client/services/lesson-info'
import type { AssignmentOption } from 'teacher-ui-client/components/assignments-admin/assignment-form'
import { assert } from '@ember/debug'

interface Signature {
  Args: {
    /** only setup for RE_READING, RE_SPELLING, RE_STORYLANDS, REX_READING and MS_LESSONS */
    precinct: Precinct
    selectAssignmentAction: (option?: AssignmentOption) => void
    selectedAssignmentOption?: AssignmentOption
  }
}

export class ActivitySelectorLessonsByGradeList extends Component<Signature> {
  @service subscriptionType!: SubscriptionTypeService

  @service lessonInfo!: LessonInfoService

  loadOptions = trackedFunction(this, async () => {
    assert(
      'unexpected precinct for lesson-by-grade-list',
      this.args.precinct === Precinct.RE_READING ||
        this.args.precinct === Precinct.RE_SPELLING ||
        this.args.precinct === Precinct.RE_STORYLANDS ||
        this.args.precinct === Precinct.REX_READING ||
        this.args.precinct === Precinct.MS_LESSONS,
    )

    await this.lessonInfo.loadLessonInfoFor([this.args.precinct])
    return this.lessonInfo.lessonsFor(this.args.precinct).map<AssignmentOption>((data) => ({
      id: String(data.id),
      label:
        'description' in data
          ? `${data.lesson_number} - ${data.name}: ${data.description}`
          : `${data.lesson_number} - ${data.name}`,
      gradePosition: data.grade_position,
      assignmentData: {
        assignment_id: String(data.lesson_number),
        assignment_type: 'lesson',
        grade_position: data.grade_position,
        grade_lesson_number: data.grade_lesson_number,
        ...('description' in data && { description: data.description }),
      },
    }))
  })

  get options() {
    return this.loadOptions.value ?? []
  }

  get componentId() {
    return guidFor(this)
  }

  @tracked currentGradePosition: number = AllGradesOption.id

  get gradePositions() {
    const gradePositions = this.options.map((option) => option.gradePosition)

    const definedGradePositions = gradePositions.filter((pos): pos is number => typeof pos === 'number')
    const uniqGradePositions = [...new Set(definedGradePositions)]
    return uniqGradePositions.sort((a, b) => a - b)
  }

  get filteredOptions() {
    return this.options.filter(
      (option) =>
        this.currentGradePosition === AllGradesOption.id || option.gradePosition === this.currentGradePosition,
    )
  }

  get optionsGroupedByGradePosition() {
    return groupBy(this.filteredOptions, 'gradePosition')
  }

  get gradePositionGroupKeys() {
    return Object.keys(this.optionsGroupedByGradePosition).map((key) => Number.parseInt(key, 10))
  }

  @action
  selectGradePosition(gradePosition: number) {
    this.currentGradePosition = gradePosition
  }

  @action
  selectOption(optionId: string) {
    const foundOption = this.options.find((option) => option.id === optionId)
    this.args.selectAssignmentAction(foundOption)
  }

  <template>
    <div class="flex justify-between md:flex-wrap">
      <div class="mb-4 px-3 md:w-full lg:w-1/2">
        <label id="{{this.componentId}}-assignment-grade-label" class="mb-1">
          {{t "grade.label"}}
        </label>
        <GradeSelect
          class="w-full"
          name="activity-grade"
          aria-labelledby="{{this.componentId}}-assignment-grade-label"
          @additionalOptions={{array AllGradesOption}}
          @gradePositions={{this.gradePositions}}
          @gradePosition={{this.currentGradePosition}}
          @updateGradePosition={{this.selectGradePosition}}
          @themeKey={{themeKey this.subscriptionType.current}}
        />
      </div>
      <div class="mb-4 px-3 md:w-full lg:w-1/2">
        <label id="{{this.componentId}}-assignment-lessons-label" class="mb-1">
          {{t "assignments.create.activityLesson"}}
        </label>
        <BaseSelect
          data-test-select-lesson
          class="w-full"
          name="activity-lesson"
          value={{or @selectedAssignmentOption.id ""}}
          aria-labelledby="{{this.componentId}}-assignment-lessons-label"
          @themeKey={{themeKey this.subscriptionType.current}}
          {{on "input" (eventValue this.selectOption)}}
        >
          <option value="">{{t "assignments.create.activityLessonPlaceholder"}}</option>
          {{#each this.gradePositionGroupKeys as |gradePosition|}}
            <optgroup label={{fullGradeName gradePosition}}>
              {{#each (get this.optionsGroupedByGradePosition gradePosition) as |optionItem|}}
                <option value={{optionItem.id}} selected={{eq optionItem.id @selectedAssignmentOption.id}}>
                  {{optionItem.label}}
                </option>
              {{/each}}
            </optgroup>
          {{/each}}
        </BaseSelect>
      </div>
    </div>
  </template>
}
